import { useState, useMemo, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Eye, Edit2, Trash2, Save, X } from "lucide-react";
import logger from "@/lib/logger";

const DATE_FIELDS = ["created_at", "updated_at", "in_time", "out_time", "issued_time", "return_time", "tentative_return_date", "date_of_receipt"];
const HIDDEN_FIELDS = ["_id", "id", "is_deleted", "audit_trail", "photo", "gate_pass_no", "location"];

function formatValue(key, value) {
  if (value === null || value === undefined || value === "") return "\u2014";
  if (DATE_FIELDS.includes(key) && typeof value === "string" && value.includes("T")) {
    return new Date(value).toLocaleString("en-IN");
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function formatLabel(key) {
  return key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

function DetailField({ label, value }) {
  return (
    <div className="py-2 border-b border-gray-100 last:border-0">
      <div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">{label}</div>
      <div className="text-sm mt-0.5 break-words">{value}</div>
    </div>
  );
}

function DetailActions({ entry, collection, isEditing, setIsEditing, editForm, setEditForm, onRefresh, onClose }) {
  const handleSave = useCallback(async () => {
    try {
      await api.put(`/entry/${collection}/${entry.id}`, editForm);
      toast.success("Entry updated");
      setIsEditing(false);
      onRefresh();
    } catch (err) {
      logger.error("Update failed:", err);
      toast.error(err.response?.data?.detail || "Update failed");
    }
  }, [collection, entry?.id, editForm, setIsEditing, onRefresh]);

  const handleDelete = useCallback(async () => {
    if (!window.confirm("Are you sure you want to delete this entry? This action is soft-delete and can be reversed.")) return;
    try {
      await api.delete(`/entry/${collection}/${entry.id}`);
      toast.success("Entry deleted");
      onClose();
      onRefresh();
    } catch (err) {
      logger.error("Delete failed:", err);
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  }, [collection, entry?.id, onClose, onRefresh]);

  if (isEditing) {
    return (
      <div className="flex gap-2 mt-4 pt-3 border-t">
        <Button onClick={handleSave} className="bg-emerald-600 hover:bg-emerald-700 h-9 text-xs flex-1" data-testid="save-edit-btn">
          <Save size={14} className="mr-1" /> Save Changes
        </Button>
        <Button variant="outline" onClick={() => setIsEditing(false)} className="h-9 text-xs" data-testid="cancel-edit-btn">
          <X size={14} className="mr-1" /> Cancel
        </Button>
      </div>
    );
  }

  return (
    <div className="flex gap-2 mt-4 pt-3 border-t">
      <Button variant="outline" onClick={() => { setEditForm({ ...entry }); setIsEditing(true); }} className="h-9 text-xs flex-1" data-testid="edit-entry-btn">
        <Edit2 size={14} className="mr-1" /> Edit
      </Button>
      <Button variant="outline" onClick={handleDelete} className="h-9 text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-entry-btn">
        <Trash2 size={14} className="mr-1" /> Delete
      </Button>
    </div>
  );
}

export default function EntryDetailDialog({ open, onClose, entry, title, collection, onRefresh }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const { user } = useAuth();

  const visibleFields = useMemo(() => {
    if (!entry) return [];
    return Object.keys(entry).filter(k => !HIDDEN_FIELDS.includes(k));
  }, [entry]);

  const handleClose = () => {
    setIsEditing(false);
    onClose();
  };

  if (!entry) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose(); }}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="entry-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Chivo" }}>
            <Eye size={18} className="text-[#1B3C87]" />
            {title || "Entry Details"}
            {entry.ref_no && <Badge variant="outline" className="font-mono text-xs">{entry.ref_no}</Badge>}
          </DialogTitle>
        </DialogHeader>

        {entry.photo && (
          <div className="mb-3">
            <img src={entry.photo} alt="Entry photo" className="w-24 h-24 rounded-lg border object-cover" />
          </div>
        )}
        {entry.gate_pass_no && (
          <div className="mb-3 text-center p-2 bg-[#1B3C87]/10 rounded">
            <div className="text-[10px] uppercase text-gray-500">Gate Pass No.</div>
            <div className="text-lg font-black text-[#1B3C87] tracking-wider">{entry.gate_pass_no}</div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0">
          {visibleFields.map(key => (
            <div key={key}>
              {isEditing ? (
                <div className="py-2 border-b border-gray-100 last:border-0">
                  <div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">{formatLabel(key)}</div>
                  <Input
                    value={editForm[key] ?? ""}
                    onChange={e => setEditForm(prev => ({ ...prev, [key]: e.target.value }))}
                    className="h-8 text-sm mt-0.5"
                    data-testid={`edit-field-${key}`}
                  />
                </div>
              ) : (
                <DetailField label={formatLabel(key)} value={formatValue(key, entry[key])} />
              )}
            </div>
          ))}
        </div>

        {(user?.role === "admin" || user?.role === "manager") && (
          <DetailActions
            entry={entry}
            collection={collection}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            editForm={editForm}
            setEditForm={setEditForm}
            onRefresh={onRefresh}
            onClose={handleClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
